import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MessageSquareWarning, Plus } from "lucide-react";
import { residentApi, errMsg } from "@/lib/api";
import { useResident } from "@/lib/residentAuth";
import { dateLabel } from "@/lib/format";
import { Card, EmptyState, ErrorState, ListSkeleton } from "@/components/common";
import { BottomSheet } from "@/components/BottomSheet";
import { Field, BigInput, Chips } from "@/components/form";
import { useResidentApi } from "@/pages/resident/useResidentApi";

const CATS = ["electrical", "plumbing", "food", "cleaning", "wifi", "other"];
const STATUS: Record<string, [string, string]> = {
  open: ["Open", "bg-rose-50 text-rose-700 border-rose-200"], in_progress: ["In progress", "bg-amber-50 text-amber-800 border-amber-200"], resolved: ["Resolved", "bg-emerald-50 text-emerald-700 border-emerald-200"],
};

function NewSheet({ open, onClose, onSaved }: { open: boolean; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({ title: "", category: "other", description: "" });
  const save = async () => {
    try {
      await residentApi.post("/resident/complaints", f);
      setF({ title: "", category: "other", description: "" });
      toast.success("Complaint sent to your PG owner");
      onSaved();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <BottomSheet open={open} onOpenChange={(o) => !o && onClose()} title="Raise a complaint" testId="resident-complaint-sheet">
      <div className="space-y-4">
        <Field label="What's the issue?"><BigInput data-testid="rc-title-input" maxLength={100} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. Geyser not working" /></Field>
        <Field label="Category"><Chips testId="rc-category" value={f.category} onChange={(v) => setF({ ...f, category: v })} options={CATS.map((c) => ({ value: c, label: c === "wifi" ? "WiFi" : c[0].toUpperCase() + c.slice(1) }))} /></Field>
        <Field label="Details (optional)"><Textarea data-testid="rc-desc-input" rows={3} maxLength={1000} className="rounded-xl text-base bg-white" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
        <Button data-testid="rc-submit-btn" disabled={!f.title.trim()} onClick={save} className="w-full h-14 rounded-xl text-base bg-[#131e2e] hover:bg-[#0b1320]">Send</Button>
      </div>
    </BottomSheet>
  );
}

function List() {
  const { data, loading, error, reload } = useResidentApi<any[]>("/resident/complaints");
  const [open, setOpen] = useState(false);
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Complaints</h1>
        <Button data-testid="rc-new-btn" onClick={() => setOpen(true)} className="h-11 rounded-full bg-[#131e2e] hover:bg-[#0b1320]"><Plus className="h-4 w-4 mr-1" />New</Button>
      </div>
      {loading && !data ? <ListSkeleton rows={3} /> : error ? <ErrorState message={error} onRetry={reload} /> : !data.length ? (
        <EmptyState testId="rc-empty" icon={MessageSquareWarning} title="No complaints yet" text="Something broken? Tell your PG owner here and track it." />
      ) : (
        <div className="space-y-2.5 pg-stagger">
          {data.map((c) => (
            <Card key={c.id} className="p-4" data-testid={`rc-item-${c.id}`}>
              <div className="flex justify-between gap-2">
                <div className="min-w-0"><p className="font-semibold">{c.title}</p><p className="text-xs text-slate-500 capitalize">{c.category} · {dateLabel(c.created_at)}</p></div>
                <span data-testid={`rc-status-${c.id}`} className={`h-fit text-xs font-semibold rounded-full border px-2.5 py-1 whitespace-nowrap ${STATUS[c.status][1]}`}>{STATUS[c.status][0]}</span>
              </div>
              {c.description && <p className="text-sm text-slate-600 mt-2">{c.description}</p>}
            </Card>
          ))}
        </div>
      )}
      <NewSheet open={open} onClose={() => setOpen(false)} onSaved={reload} />
    </div>
  );
}

export default function ResidentComplaints() {
  const { resident } = useResident();
  if (!resident?.stay)
    return <EmptyState testId="rc-nostay" icon={MessageSquareWarning} title="Join a PG first" text="Complaints go to your PG owner once you're staying." action={<Link to="/resident/search" className="font-semibold text-[#131e2e] underline">Find a PG</Link>} />;
  return <List />;
}
